import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import { MdPhoneInTalk } from "react-icons/md";
import Axios from "../../_services/caller.service";
import { useCabineContext } from "../../context/CabineContext";
import styles from "./SupportRequestForm.module.scss";

const CONTACT_PREFILL_EXCEPTIONS = [
  {
    displayName: "Nicolas Dezaire",
    email: "nicolas.dezaire@vyv3.fr",
  },
];

const normalizeForCompare = (value) => String(value || "").trim().toLowerCase();

const shouldSkipContactPrefill = (ownerProfile) =>
  CONTACT_PREFILL_EXCEPTIONS.some(
    ({ displayName, email }) =>
      normalizeForCompare(ownerProfile?.display_name) ===
        normalizeForCompare(displayName) &&
      normalizeForCompare(ownerProfile?.user_email) === normalizeForCompare(email)
  );

const normalizeSearchValue = (value) => String(value || "").toLowerCase().trim();

const CabineField = ({ t, cabine, formData, onChange }) => {
  const [cabineSearch, setCabineSearch] = useState("");
  const [isCabineSelectOpen, setIsCabineSelectOpen] = useState(false);
  const searchValue = normalizeSearchValue(cabineSearch);

  const authorizedCabines = useMemo(
    () => (Array.isArray(cabine) ? cabine : []),
    [cabine]
  );

  const displayedCabines = useMemo(() => {
    const filteredCabines = searchValue
      ? authorizedCabines.filter((c) => {
          const cabName = normalizeSearchValue(c.cabname || `Cabine ${c.id}`);
          const cabId = normalizeSearchValue(c.id);

          return cabName.includes(searchValue) || cabId.includes(searchValue);
        })
      : authorizedCabines;

    const selectedCabine = authorizedCabines.find(
      (c) => String(c.id) === String(formData.cabineId)
    );

    if (
      selectedCabine &&
      !filteredCabines.some((c) => String(c.id) === String(selectedCabine.id))
    ) {
      return [selectedCabine, ...filteredCabines];
    }

    return filteredCabines;
  }, [authorizedCabines, formData.cabineId, searchValue]);

  return (
    <label className={styles.field}>
      <span>{t("support_request.field_cabine")}</span>
      <input
        type="search"
        value={cabineSearch}
        onFocus={() => setIsCabineSelectOpen(true)}
        onChange={(e) => {
          setCabineSearch(e.target.value);
          setIsCabineSelectOpen(true);
        }}
        placeholder={t("support_request.search_cabine")}
      />
      <select
        name="cabineId"
        value={formData.cabineId}
        onFocus={() => setIsCabineSelectOpen(true)}
        onBlur={() => setIsCabineSelectOpen(false)}
        onChange={(e) => {
          onChange(e);
          setIsCabineSelectOpen(false);
        }}
        size={
          isCabineSelectOpen
            ? Math.min(Math.max(displayedCabines.length + 1, 2), 8)
            : undefined
        }
        required
      >
        <option value="">{t("support_request.select_cabine")}</option>
        {displayedCabines.length > 0 ? (
          displayedCabines.map((c) => (
            <option key={c.id} value={c.id}>
              {c.cabname || `Cabine ${c.id}`}
            </option>
          ))
        ) : (
          <option value="" disabled>
            {t("support_request.no_cabine_found")}
          </option>
        )}
      </select>
    </label>
  );
};

const SupportRequestForm = ({ ownerProfile, ownerId, onClose }) => {
  const { t } = useTranslation();
  const { cabine } = useCabineContext();
  const skipContactPrefill = shouldSkipContactPrefill(ownerProfile);
  const [formData, setFormData] = useState({
    name: skipContactPrefill ? "" : ownerProfile?.display_name || "",
    email: skipContactPrefill ? "" : ownerProfile?.user_email || "",
    phone: ownerProfile?.phone || "",
    cabineId: "",
    centerName: "",
    address: "",
    address2: "",
    zipCode: "",
    city: "",
    country: "",
    category: "",
    priority: "Normale",
    subject: "",
    description: "",
    message: "",
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successRef, setSuccessRef] = useState("");
  const supportPhoneDisplay = t("faq.phone_number");

  const supportPhoneHref = useMemo(() => {
    const phoneDigits = String(supportPhoneDisplay || "").replace(/\D/g, "");

    if (!phoneDigits) {
      return "tel:+33261720602";
    }

    if (phoneDigits.startsWith("0") && phoneDigits.length === 10) {
      return `tel:+33${phoneDigits.slice(1)}`;
    }

    if (phoneDigits.startsWith("33")) {
      return `tel:+${phoneDigits}`;
    }

    return `tel:+${phoneDigits}`;
  }, [supportPhoneDisplay]);

  const selectedCabine = cabine?.find(
    (c) => String(c.id) === String(formData.cabineId)
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchCabineAddress = useCallback(async (cabId) => {
    if (!cabId) return;
    setAddressLoading(true);
    try {
      const res = await Axios.get(`/calibrationRequest/address/${cabId}`);
      if (!res.data.error && res.data.data) {
        const { address, address2, zipCode, city, country, phone } = res.data.data;
        setFormData((prev) => ({
          ...prev,
          address: address || "",
          address2: address2 || "",
          zipCode: zipCode || "",
          city: city || "",
          country: country || "",
          ...(phone ? { phone } : {}),
        }));
      }
    } catch (err) {
      // Pas bloquant, l'utilisateur peut remplir manuellement.
    } finally {
      setAddressLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.cabineId) {
      fetchCabineAddress(formData.cabineId);
    }
  }, [formData.cabineId, fetchCabineAddress]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: (prev.name || skipContactPrefill)
        ? prev.name
        : ownerProfile?.display_name || "",
      email: (prev.email || skipContactPrefill)
        ? prev.email
        : ownerProfile?.user_email || "",
      phone: prev.phone || ownerProfile?.phone || "",
    }));
  }, [
    ownerProfile?.display_name,
    ownerProfile?.phone,
    ownerProfile?.user_email,
    skipContactPrefill,
  ]);

  useEffect(() => {
    if (selectedCabine) {
      setFormData((prev) => ({
        ...prev,
        centerName: selectedCabine.cabname || "",
      }));
    }
  }, [selectedCabine]);

  const buildPayload = () => ({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    cabineName:
      formData.centerName ||
      selectedCabine?.cabname ||
      `Cabine ${formData.cabineId}`,
    address: [formData.address, formData.address2].filter(Boolean).join(", "),
    zipCode: formData.zipCode,
    city: formData.city,
    country: formData.country,
    category: formData.category,
    priority: formData.priority,
    subject: formData.subject,
    description: formData.description,
    message: formData.message,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await Axios.post(`/supportRequest/${ownerId}`, buildPayload());
      setSuccessRef(true);
    } catch (err) {
      setError(t("support_request.error"));
    } finally {
      setLoading(false);
    }
  };

  if (successRef) {
    return (
      <div className={styles.successMsg}>
        <p>{t("support_request.success")}</p>
        <button className={styles.doneBtn} onClick={onClose} type="button">
          {t("support_request.close")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <button className={styles.backToMenu} onClick={onClose} type="button">
        <FaArrowLeft /> {t("support_request.back")}
      </button>

      <div className={styles.callReminder}>
        <p>{t("support_request.call_reminder")}</p>
        <a className={styles.callReminderLink} href={supportPhoneHref}>
          <MdPhoneInTalk aria-hidden="true" />
          <span>{supportPhoneDisplay}</span>
        </a>
      </div>

      <div className={styles.formLayout}>
        <div className={styles.headerInfo}>
          <h4>{t("support_request.form_title")}</h4>
          <p>{t("support_request.form_desc")}</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <CabineField
            t={t}
            cabine={cabine}
            formData={formData}
            onChange={handleChange}
          />

          <label className={styles.field}>
            <span>{t("support_request.field_name")}</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_email")}</span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_phone")}</span>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t("support_request.phone_placeholder")}
            />
          </label>

          <fieldset className={styles.addressGroup}>
            <legend>{t("support_request.field_address_title")}</legend>
            {addressLoading && (
              <p className={styles.addressLoading}>
                {t("support_request.loading_address")}
              </p>
            )}
            <label className={styles.field}>
              <span>{t("support_request.field_center_name")}</span>
              <input
                type="text"
                name="centerName"
                value={formData.centerName}
                onChange={handleChange}
                required
              />
            </label>
            <label className={styles.field}>
              <span>{t("support_request.field_address")}</span>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </label>
            <label className={styles.field}>
              <span>{t("support_request.field_address2")}</span>
              <input
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
              />
            </label>
            <div className={styles.addressRow}>
              <label className={styles.field}>
                <span>{t("support_request.field_zipcode")}</span>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </label>
              <label className={styles.field}>
                <span>{t("support_request.field_city")}</span>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </label>
            </div>
            <label className={styles.field}>
              <span>{t("support_request.field_country")}</span>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </label>
          </fieldset>

          <label className={styles.field}>
            <span>{t("support_request.field_category")}</span>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">{t("support_request.select_category")}</option>
              <option value="Probleme logiciel">{t("support_request.category_software")}</option>
              <option value="Acces compte">{t("support_request.category_account")}</option>
              <option value="Calibration">{t("support_request.category_calibration")}</option>
              <option value="Licence">{t("support_request.category_license")}</option>
              <option value="Question generale">{t("support_request.category_general")}</option>
              <option value="Autre">{t("support_request.category_other")}</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_priority")}</span>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              required
            >
              <option value="Normale">{t("support_request.priority_normal")}</option>
              <option value="Urgente">{t("support_request.priority_urgent")}</option>
              <option value="Bloquante">{t("support_request.priority_blocking")}</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_subject")}</span>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_description")}</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("support_request.field_message")}</span>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              placeholder={t("support_request.message_placeholder")}
            />
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <button className={styles.submitBtn} type="submit" disabled={loading}>
            {loading ? t("support_request.sending") : t("support_request.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportRequestForm;
